export type AnonymousPrimitive = string | number | boolean | null;

export type AnonymousValue =
  | AnonymousPrimitive
  | AnonymousValue[]
  | { [key: string]: AnonymousValue };

export type AnonymousAnalyticsEvent = {
  eventName: string;
  properties: Record<string, AnonymousValue>;
};

export const ANONYMOUS_INCLUDED_PROPERTY_KEYS = [
  'account_creation_method',
  'action',
  'action_source',
  'additional_features',
  'ad_tracking_consent',
  'amount',
  'amount_to_pay',
  'amount_to_pay_nzd',
  'booking_error',
  'booking_error_recoverability',
  'booking_error_source',
  'booking_fee',
  'booking_mode',
  'booking_selected_date',
  'booking_selected_date_time',
  'booking_selected_date_utc',
  'booking_selected_number_of_people',
  'booking_selected_time',
  'booking_selected_time_utc',
  'booking_status',
  'booking_type',
  'button_copy',
  'button_name',
  'button_type',
  'button_value',
  'category',
  'city_id',
  'city_name',
  'component_detail',
  'component_id',
  'component_name',
  'component_type',
  'contact_permission',
  'content_name',
  'country_id',
  'country_name',
  'currency',
  'date',
  'date_utc',
  'date_time',
  'description',
  'discount',
  'error',
  'error_message',
  'error_visible',
  'experimentId',
  'experiment_name',
  'failure_reason',
  'filtered_additional_options',
  'filtered_city_id',
  'filtered_city_name',
  'filtered_country_id',
  'filtered_country_name',
  'filtered_cuisine',
  'filtered_date',
  'filtered_date_time',
  'filtered_date_utc',
  'filtered_dietary',
  'filtered_entry_requirements',
  'filtered_features',
  'filtered_good_for',
  'filtered_number_of_people',
  'filtered_price',
  'filtered_region_id',
  'filtered_session',
  'filtered_sort',
  'filtered_suburb',
  'filtered_suburbs',
  'filtered_time',
  'filtered_time_utc',
  'filtered_time_range',
  'filtered_venue_type',
  'filtered_view_mode',
  'filters_applied',
  'first_table_conditions',
  'focus_duration',
  'food',
  'form_name',
  'growthbook_experiment_id',
  'growthbook_variation_id',
  'impression',
  'index',
  'initialized_from',
  'is_new',
  'label',
  'label_href',
  'link_name',
  'link_copy',
  'link_type',
  'login_method',
  'map_open',
  'method',
  'navigated_from',
  'new_account',
  'new_payment_intent_complete',
  'news',
  'nth_booking',
  'number_of_people',
  'option_selected_id',
  'option_selected_name',
  'options',
  'page_name',
  'page_id',
  'page_type',
  'payment_method',
  'potential_points',
  'price',
  'price_nzd',
  'promo_code_used',
  'promotion_name',
  'query',
  'quantity',
  'reason',
  'region_id',
  'region_name',
  'region_ranking',
  'remaining_restaurants_to_search',
  'restaurant_country_id',
  'restaurant_conditions',
  'restaurant_id',
  'restaurant_cuisine',
  'restaurant_cuisines',
  'restaurant_dietary',
  'restaurant_entry_requirements',
  'restaurant_features',
  'restaurant_good_for',
  'restaurant_name',
  'restaurant_price',
  'restaurant_rating',
  'restaurant_region_id',
  'restaurant_region_name',
  'restaurant_reviews_count',
  'restaurant_suburb_id',
  'restaurant_suburb_name',
  'restaurant_venue_type',
  'restaurants',
  'restaurants_searched',
  'review_rating',
  'rrp_nzd',
  'search_value',
  'section',
  'selected_payment_method',
  'service',
  'session_selected',
  'sessions_available',
  'site_id',
  'source',
  'source_page',
  'source_page_id',
  'source_page_section',
  'status',
  'suburb_id',
  'suburb_name',
  'suburb_skipped',
  'suburbs_selected_count',
  'table_type',
  'time',
  'time_since_search_started',
  'time_to_fetch',
  'time_to_first_response',
  'time_to_first_visible',
  'time_to_last_response',
  'time_utc',
  'total_amount_due',
  'total_restaurants_visible',
  'total_reviews',
  'total_searchable_restaurants',
  'transaction_value',
  'transaction_value_nzd',
  'type',
  'user_in_experiment',
  'variant_id',
  'variant_name',
  'variant_value',
  'variation',
  'variationId',
  'view_mode',
] as const;

// Audit inventory only. Runtime filtering is intentionally controlled by the included-property allowlist above so there is a single enforcement source.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ANONYMOUS_REVIEWED_EXCLUDED_PROPERTY_KEYS = [
  'anonymous_id',
  'as_path',
  'availability_id',
  'booking_id',
  'booking_reference',
  'coalesced_id',
  'content_category',
  'content_ids',
  'content_type',
  'customer_cancellation_reason',
  'device_id',
  'dietary_requirements',
  'dined_before',
  'email',
  'event_id',
  'event_source_url',
  'fbc',
  'fbp',
  'first_name',
  'form_email',
  'form_message',
  'frequent_foodies_points_balance_last_12_months',
  'frequent_foodies_tier',
  'href',
  'input_value',
  'items',
  'last_name',
  'name',
  'path_name',
  'phone',
  'phone_verified',
  'private_message',
  'prize_value',
  'promo_code',
  'public_message',
  'slot_id',
  'stripe_token',
  'to',
  'transaction_id',
  'transaction_tax',
  'user_email',
  'user_has_booked_before',
  'user_id',
  'user_last_booking_date',
  'user_name',
  'user_phone',
  'user_phone_number',
  'user_signup_date',
  'user_total_booking_spend_in_nzd',
  'user_total_bookings_made',
  'user_total_credit_spent_*',
  'uuid_c1',
  'value',
] as const;

const includedPropertyKeys = new Set<string>(ANONYMOUS_INCLUDED_PROPERTY_KEYS);

export const ANONYMOUS_INCLUDED_EVENT_NAMES = [
  'account_viewed',
  'account_creation_failed',
  'account_creation_page_viewed',
  'account_login_page_viewed',
  'home_viewed',
  'marketing_page_viewed',
  'restaurateurs_page_viewed',
  'bookings_viewed',
  'share_viewed',
  'map_viewed',
  'modal_viewed',
  'booking_flow_viewed',
  'booking_page_viewed',
  'experiment_viewed',
  'exit_modal_opened',
  'exit_modal_closed',
  'exit_modal_cta_clicked',
  'restaurant_viewed',
  'list_viewed',
  'filter_applied',
  'filters_searched',
  'date_selected',
  'dine_now_banner_dismissed',
  'dine_now_banner_learn_more_clicked',
  'dine_now_banner_viewed',
  'location_selected',
  'button_clicked',
  'clicks_menu_media_button',
  'clicks_photos_media_button',
  'link_clicked',
  'component_interaction',
  'restaurant_impression',
  'copied_to_clipboard',
  'how_it_works_opened',
  'suburb_selector_dismissed',
  'suburb_selector_save_clicked',
  'suburb_selector_submitted',
  'booking_initialized',
  'booking_status',
  'booking_error',
  'submit_reservation_payment',
  'product_added_to_cart',
  'product_removed_from_cart',
  'purchase',
  'booking_cancelled',
  'booking_revoked',
  'account_created',
  'account_creation_updated',
  'account_login',
  'account_logout',
  'competition_entered',
  'form_submitted',
  'form_updated',
  'search',
  'search_query_stats',
  'search_query_stopped',
  'cookie_consent_rejected',
] as const;

export type AnonymousAnalyticsEventName = (typeof ANONYMOUS_INCLUDED_EVENT_NAMES)[number];

export const ANONYMOUS_JITTERED_EVENT_NAMES = [
  'account_creation_failed',
  'booking_initialized',
  'booking_status',
  'booking_error',
  'submit_reservation_payment',
  'purchase',
  'account_created',
  'account_creation_updated',
  'account_login',
  'competition_entered',
  'form_submitted',
  'search_query_stopped',
] as const satisfies readonly AnonymousAnalyticsEventName[];

const includedEventNames = new Set<string>(ANONYMOUS_INCLUDED_EVENT_NAMES);
const jitteredEventNames = new Set<string>(ANONYMOUS_JITTERED_EVENT_NAMES);

export const isAnonymousTimestampJitterEvent = (eventName: string): boolean =>
  jitteredEventNames.has(eventName);

const sanitizeAnonymousValue = (value: unknown): AnonymousValue | undefined => {
  if (value === null) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (Array.isArray(value)) {
    return value
      .map(sanitizeAnonymousValue)
      .filter((item): item is AnonymousValue => item !== undefined);
  }

  if (typeof value === 'object' && value) {
    return Object.entries(value).reduce<Record<string, AnonymousValue>>(
      (sanitizedValue, [key, nestedValue]) => {
        if (!includedPropertyKeys.has(key)) {
          return sanitizedValue;
        }

        const sanitizedNestedValue = sanitizeAnonymousValue(nestedValue);
        if (sanitizedNestedValue !== undefined) {
          sanitizedValue[key] = sanitizedNestedValue;
        }

        return sanitizedValue;
      },
      {}
    );
  }

  return undefined;
};

const getPurchaseTransactionValue = (properties: Record<string, unknown>): number | undefined => {
  const transactionValue = properties.transaction_value ?? properties.value ?? properties.amount;

  if (typeof transactionValue === 'number') {
    return Number.isFinite(transactionValue) ? transactionValue : undefined;
  }

  if (typeof transactionValue === 'string' && transactionValue.trim()) {
    const parsedTransactionValue = Number(transactionValue);
    return Number.isFinite(parsedTransactionValue) ? parsedTransactionValue : undefined;
  }

  return undefined;
};

export const buildAnonymousAnalyticsEvent = (
  eventName: string,
  properties: Record<string, unknown>
): AnonymousAnalyticsEvent | null => {
  if (!includedEventNames.has(eventName)) {
    return null;
  }

  const candidateProperties =
    eventName === 'purchase'
      ? {
          ...properties,
          transaction_value: getPurchaseTransactionValue(properties),
        }
      : properties;

  const anonymousProperties = Object.entries(candidateProperties).reduce<
    Record<string, AnonymousValue>
  >((allowedProperties, [key, value]) => {
    if (includedPropertyKeys.has(key)) {
      const sanitizedValue = sanitizeAnonymousValue(value);
      if (sanitizedValue !== undefined) {
        allowedProperties[key] = sanitizedValue;
      }
    }

    return allowedProperties;
  }, {});

  anonymousProperties.action_source = 'website';
  const siteId = Number(process.env.NEXT_PUBLIC_SITE_ID);
  if (Number.isFinite(siteId)) {
    anonymousProperties.site_id = siteId;
  }

  return {
    eventName,
    properties: anonymousProperties,
  };
};
